/**
 * Pure helpers shared by the attachment viewers (AttachmentList, AttachmentTab,
 * AttachmentPreviewOverlay). Kept out of the .tsx files so React fast refresh
 * sees only components there.
 */
import type { AttachmentMeta } from '../../types/portal';
import { triggerBlobDownload } from '../../utils/triggerDownload';

/** Gmail often sends PDFs as application/octet-stream; fall back to the extension. */
export function isPdfAttachment(mimeType: string, filename: string): boolean {
  return mimeType === 'application/pdf' || (
    mimeType === 'application/octet-stream' && filename.toLowerCase().endsWith('.pdf')
  );
}

export function isPreviewableAttachment(mimeType: string, filename: string): boolean {
  return isPdfAttachment(mimeType, filename) || mimeType.startsWith('image/');
}

/**
 * Trigger a browser download of an already-fetched blob URL. The anchor is
 * never attached, so the recorder sees only the Download control's own row.
 */
export function downloadBlobUrl(blobUrl: string, filename: string): void {
  triggerBlobDownload(blobUrl, filename);
}

/**
 * Where a click on an attachment card lands. Recorded as values.mode on the
 * attachment_open row (the recorder's outcome field is a closed vocabulary:
 * previewable files record outcome 'open', downloads 'request').
 */
export type AttachmentOpenMode = 'inline' | 'preview' | 'download';

export function attachmentOpenMode(
  att: Pick<AttachmentMeta, 'mime_type' | 'filename'>,
  inlineAvailable: boolean,
): AttachmentOpenMode {
  if (!isPreviewableAttachment(att.mime_type, att.filename)) return 'download';
  return inlineAvailable ? 'inline' : 'preview';
}
